import { Capacitor } from "@capacitor/core";
import type { UXCamConfiguration } from "cordova-uxcam";
import { UXCamConfig } from "../constants/UXCamConfig";

/**
 * cordova-uxcam 3.8.x registers as a Capacitor plugin exposed at
 * `Capacitor.Plugins.UXCam` — it does NOT set the legacy `window.UXCam` global
 * the older integration relied on. So all UXCam access goes through Capacitor.
 * The plugin is only present on native builds; on web/Cypress it's undefined
 * and every helper here is a no-op.
 */
interface UXCamPlugin {
    optIntoSchematicRecordings(): Promise<void>;
    startWithConfiguration(options: {
        configuration: UXCamConfiguration;
    }): Promise<{ sessionUrl: string }>;
    stopSessionAndUploadData(): Promise<void>;
    setUserIdentity(options: { identity: string }): Promise<void>;
    tagScreenName(options: { screenName: string }): Promise<void>;
    isRecording(): Promise<{ isRecording: boolean }>;
    urlForCurrentSession(): Promise<{ url: string }>;
}

function getUXCam(): UXCamPlugin | undefined {
    return (Capacitor as any).Plugins?.UXCam;
}

/**
 * The in-flight/settled startWithConfiguration call. Kept at module scope so a
 * second startUXCam() (e.g. the app-open start followed by a post-login
 * identify) reuses the same session instead of spinning up a duplicate, and so
 * identifyUXCamUser can sequence the identity after the session exists. Reset
 * on failure so a later call can retry the start, and on logout
 * (resetUXCamSession) so the next user gets their own session.
 */
let startPromise: Promise<void> | undefined;

/**
 * Start UXCam recording without a user identity.
 *
 * REFSIX-776: called on every app open — before the user has registered or
 * logged in, so session replay captures pre-registration behaviour and
 * drop-off, and for an already-authenticated user, whose relaunches would
 * otherwise never be recorded at all. Idempotent, and a no-op when the native
 * plugin isn't present (web / Cypress), so it's safe to call unconditionally.
 * Returns the start promise so callers can sequence work after the session
 * exists.
 */
export function startUXCam(): Promise<void> {
    const uxcam = getUXCam();
    if (!uxcam) return Promise.resolve();
    if (!startPromise) {
        uxcam
            .optIntoSchematicRecordings()
            .catch((err) => console.warn("UXCam optIn failed", err));
        startPromise = beginSession(uxcam);
    }
    return startPromise;
}

/** Open a session; on failure clear the latch so a later call can retry. */
function beginSession(uxcam: UXCamPlugin): Promise<void> {
    return uxcam
        .startWithConfiguration({ configuration: UXCamConfig })
        .then(() => undefined)
        .catch((err) => {
            console.warn("UXCam start failed", err);
            startPromise = undefined;
        });
}

/**
 * Associate the running UXCam session with a username. Ensures recording has
 * been started first (defensive) and only attaches the identity once the
 * session start has settled, so the identity can't race a first-ever start
 * and be dropped.
 */
export function identifyUXCamUser(username: string): void {
    const uxcam = getUXCam();
    if (!uxcam) return;
    startUXCam().then(() =>
        uxcam
            .setUserIdentity({ identity: username })
            .catch((err) => console.warn("UXCam identify failed", err))
    );
}

/**
 * End the current UXCam session and begin a fresh anonymous one.
 *
 * Called from the logout flow: without it the module-scope `startPromise`
 * latch keeps the logged-out user's session running, so the next account to
 * log in on the same device gets `setUserIdentity` applied to the previous
 * user's session and both users' activity ends up in one replay. Restarting
 * immediately (rather than just stopping) keeps the post-logout journey — back
 * on the auth screens — recorded, which is the drop-off REFSIX-776 is about.
 */
export function resetUXCamSession(): Promise<void> {
    const uxcam = getUXCam();
    if (!uxcam) return Promise.resolve();

    const previousStart = startPromise;
    // Swap the latch to the whole stop→restart chain in ONE synchronous
    // assignment. A startUXCam()/identifyUXCamUser() that lands mid-reset
    // (a quick re-login during logout) then sequences after the chain,
    // instead of opening a fresh session that the stop below would tear
    // down — which would leave recording off for the rest of the app
    // lifetime, since that caller's latch would sit there settled.
    startPromise = (async () => {
        if (previousStart) {
            try {
                await previousStart;
                await uxcam.stopSessionAndUploadData();
            } catch (err) {
                console.warn("UXCam session stop failed", err);
            }
        }
        await beginSession(uxcam);
    })();
    return startPromise;
}

export function uxCamTagScreen(screenName: string): void {
    const uxcam = getUXCam();
    if (!uxcam) return;
    uxcam
        .tagScreenName({ screenName })
        .catch((err) => console.warn("UXCam tagScreen failed", err));
}

export interface UXCamStatus {
    /** false on web/Cypress, where neither the native plugin nor the mock is registered. */
    available: boolean;
    /** true when answered by the local stand-in (REACT_APP_MOCK_UXCAM), not the real SDK. */
    mock: boolean;
    recording: boolean;
    sessionUrl: string;
}

/**
 * Live recording state, for the Developer Tools panel. Lets a manual tester
 * confirm on-device that a session is actually being captured (and grab its
 * replay URL) instead of waiting to see whether one turns up in the UXCam
 * dashboard afterwards.
 */
export async function getUXCamStatus(): Promise<UXCamStatus> {
    const uxcam = getUXCam();
    if (!uxcam) {
        return {
            available: false,
            mock: false,
            recording: false,
            sessionUrl: "",
        };
    }
    const mock = !!(uxcam as UXCamPlugin & { __isMock?: boolean }).__isMock;

    try {
        const [{ isRecording }, { url }] = await Promise.all([
            uxcam.isRecording(),
            uxcam.urlForCurrentSession(),
        ]);
        return { available: true, mock, recording: isRecording, sessionUrl: url };
    } catch (err) {
        console.warn("UXCam status lookup failed", err);
        return { available: true, mock, recording: false, sessionUrl: "" };
    }
}
